/**
 * Per-device UI preferences. Today that is only the theme.
 *
 * The theme is `light`, `dark` or `system`, defaulting to `system`. It is
 * persisted under `mypocketgym-theme` as the PLAIN string, not Zustand's JSON
 * envelope, because the inline pre-paint script in `index.html` reads that
 * same key before any module runs — and because installs from before this
 * store already hold that format. Change either side and returning users
 * flash the system colour, or lose their choice.
 *
 * Precedence at startup: `?theme=`, then storage, then `system`. The URL is
 * how a screenshot run asks for a theme deterministically; `setTheme` drops it
 * again, or picking a theme on a page opened with one would appear to do
 * nothing. It drops it THROUGH the router, via the writer `app/router.tsx`
 * registers with `setThemeUrlWriter`: `@tanstack/history` caches its own
 * `currentLocation` and updates it only on its own push/replace and on
 * `popstate`, so a raw `history.replaceState` is invisible to the router,
 * `theme` stays in its search, and the next `navigate()` writes the stale
 * param back into the URL — where a reload then overrides the choice the
 * person just made. This store is still created before the router exists, so
 * until a writer is registered `setTheme` falls back to the raw
 * `history.replaceState` and behaves as it always did.
 *
 * The storage adapter is synchronous on purpose, so the store is hydrated the
 * moment it is created and the first render already has the stored value.
 * Storage can throw (private mode, blocked site data, a WebView with DOM
 * storage off); every access is wrapped, and the adapter keeps the last value
 * in memory so a choice made with storage off still holds for the session.
 */
import { useSyncExternalStore } from "react";
import { useStore } from "zustand";
import { type PersistStorage, persist } from "zustand/middleware";
import { createStore } from "zustand/vanilla";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "mypocketgym-theme";
export const DARK_QUERY = "(prefers-color-scheme: dark)";

export interface PrefsState {
  setTheme: (next: ThemePreference) => void;
  theme: ThemePreference;
}

type Persisted = Pick<PrefsState, "theme">;

/** The slice of `window` this store touches, so a test can hand in fakes. */
export interface PrefsEnvironment {
  /** `location.href` now. */
  href: () => string;
  /**
   * Rewrites the URL without navigating (`history.replaceState`). Used until
   * a `setThemeUrlWriter` writer exists, and by every test that has none.
   */
  replaceUrl: (url: string) => void;
  storage: () => Pick<Storage, "getItem" | "setItem" | "removeItem">;
}

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === "light" || value === "dark" || value === "system";
}

/** The `?theme=` of a URL, when it names a valid preference. */
export function themeFromUrl(href: string): ThemePreference | null {
  const value = new URL(href).searchParams.get("theme");
  return isThemePreference(value) ? value : null;
}

/** The URL with `?theme=` removed, or `null` when it carries none. */
export function urlWithoutTheme(href: string): string | null {
  const url = new URL(href);
  if (!url.searchParams.has("theme")) {
    return null;
  }
  url.searchParams.delete("theme");
  return url.toString();
}

export function resolveTheme(
  preference: ThemePreference,
  systemIsDark: boolean
): ResolvedTheme {
  if (preference === "system") {
    return systemIsDark ? "dark" : "light";
  }
  return preference;
}

/**
 * A `persist` storage that reads and writes the raw preference string, with
 * an in-memory copy for when the real storage throws.
 */
export function rawThemeStorage(
  storage: PrefsEnvironment["storage"]
): PersistStorage<Persisted> {
  let sessionChoice: ThemePreference | null = null;
  return {
    getItem: (name) => {
      try {
        const stored = storage().getItem(name);
        if (isThemePreference(stored)) {
          return { state: { theme: stored }, version: 0 };
        }
      } catch {
        // Falls through to this session's choice, or nothing.
      }
      return sessionChoice
        ? { state: { theme: sessionChoice }, version: 0 }
        : null;
    },
    setItem: (name, value) => {
      sessionChoice = value.state.theme;
      try {
        storage().setItem(name, value.state.theme);
      } catch {
        // Not persisted: `sessionChoice` carries it until the app reloads.
      }
    },
    removeItem: (name) => {
      sessionChoice = null;
      try {
        storage().removeItem(name);
      } catch {
        // Nothing stored that could be removed.
      }
    },
  };
}

/**
 * How the URL loses its `?theme=`, once something knows how to write a URL
 * the router can see. Late-bound because the store is created first: the
 * fallback is `PrefsEnvironment.replaceUrl`, the raw `history.replaceState`.
 */
let themeUrlWriter: (() => void) | null = null;

/**
 * Registers the router's way of dropping `?theme=` (see the header). Returns
 * a function that unregisters it again, which only a test uses.
 */
export function setThemeUrlWriter(write: () => void): () => void {
  themeUrlWriter = write;
  return () => {
    if (themeUrlWriter === write) {
      themeUrlWriter = null;
    }
  };
}

export function createPrefsStore(env: PrefsEnvironment) {
  const fromUrl = themeFromUrl(env.href());
  return createStore<PrefsState>()(
    persist(
      (set) => ({
        theme: fromUrl ?? "system",
        setTheme: (next) => {
          set({ theme: next });
          const stripped = urlWithoutTheme(env.href());
          if (stripped === null) {
            return;
          }
          if (themeUrlWriter) {
            themeUrlWriter();
            return;
          }
          env.replaceUrl(stripped);
        },
      }),
      {
        name: THEME_STORAGE_KEY,
        storage: rawThemeStorage(env.storage),
        partialize: (state) => ({ theme: state.theme }),
        // `?theme=` at startup beats whatever storage holds.
        merge: (persisted, current) => {
          if (fromUrl !== null) {
            return current;
          }
          const theme = (persisted as Partial<Persisted> | undefined)?.theme;
          return isThemePreference(theme) ? { ...current, theme } : current;
        },
      }
    )
  );
}

const browserEnvironment: PrefsEnvironment = {
  href: () => window.location.href,
  replaceUrl: (url) =>
    window.history.replaceState(window.history.state, "", url),
  storage: () => window.localStorage,
};

// Outside a browser (a Node test importing this module) there is nothing to
// read; the store still works, in memory, at `system`.
const inertEnvironment: PrefsEnvironment = {
  href: () => "http://localhost/",
  replaceUrl: () => undefined,
  storage: () => {
    throw new Error("no storage outside a browser");
  },
};

const inBrowser = typeof window !== "undefined";

export const prefsStore = createPrefsStore(
  inBrowser ? browserEnvironment : inertEnvironment
);

// Another tab of the same origin changing the preference.
if (inBrowser) {
  window.addEventListener("storage", (event) => {
    if (event.key === THEME_STORAGE_KEY) {
      prefsStore.persist.rehydrate();
    }
  });
}

export function usePrefs<T>(selector: (state: PrefsState) => T): T {
  return useStore(prefsStore, selector);
}

function subscribeSystem(onChange: () => void): () => void {
  const media = inBrowser ? window.matchMedia?.(DARK_QUERY) : undefined;
  media?.addEventListener("change", onChange);
  return () => media?.removeEventListener("change", onChange);
}

/** Whether a `matchMedia` says the OS is dark; no `matchMedia` means light. */
export function systemPrefersDark(
  matchMedia: ((query: string) => Pick<MediaQueryList, "matches">) | undefined
): boolean {
  return matchMedia?.(DARK_QUERY).matches ?? false;
}

function readSystemDark(): boolean {
  return inBrowser ? systemPrefersDark(window.matchMedia?.bind(window)) : false;
}

/** The theme actually drawn: `system` resolved, following the OS live. */
export function useResolvedTheme(): ResolvedTheme {
  const theme = usePrefs((state) => state.theme);
  const dark = useSyncExternalStore(
    subscribeSystem,
    readSystemDark,
    () => false
  );
  return resolveTheme(theme, dark);
}
